import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, Modal, Form, Input, message, Popconfirm, Space, Row, Col } from 'antd';
import AdaptiveTable from '../components/AdaptiveTable';
import FilterDrawer from '../components/FilterDrawer';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { supplierApi, type SupplierDTO, type SupplierContactDTO } from '../api/kfc';
import SupplierImportModal from '../components/SupplierImportModal';
import { clientTablePagination } from '../utils/tablePagination';

const contactSummary = (contacts?: SupplierContactDTO[]) => {
  if (!contacts?.length) return '-';
  const first = contacts[0];
  const name = first.contactName || '';
  const extra = contacts.length > 1 ? ` 等${contacts.length}人` : '';
  return `${name}${extra}`.trim() || '-';
};

const SupplierManage: React.FC = () => {
  const [list, setList] = useState<SupplierDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<{ name?: string; code?: string; contact?: string }>({});
  const [modalOpen, setModalOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    supplierApi
      .list()
      .then((res) => setList(res.data))
      .catch(() => {
        setList([]);
        message.warning('无法加载供应商列表');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  /** 名称/标识/联系人三条件 AND 组合，大小写不敏感包含匹配；legacy 联系人已由后端统一转换进 contacts */
  const filteredList = useMemo(() => {
    const name = filter.name?.trim().toLowerCase();
    const code = filter.code?.trim().toLowerCase();
    const contact = filter.contact?.trim().toLowerCase();
    return list.filter((s) => {
      if (name && !s.supplierName?.toLowerCase().includes(name)) return false;
      if (code && !s.supplierId?.toLowerCase().includes(code)) return false;
      if (contact && !(s.contacts || []).some((c) => c.contactName?.toLowerCase().includes(contact)))
        return false;
      return true;
    });
  }, [list, filter]);

  const openAdd = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({ contacts: [] });
    setModalOpen(true);
  };

  const openEdit = (r: SupplierDTO) => {
    if (!r.id) return;
    setEditingId(r.id);
    supplierApi
      .get(r.id)
      .then((res) => {
        const s = res.data;
        form.setFieldsValue({
          supplierId: s.supplierId,
          supplierName: s.supplierName,
          address: s.address,
          contacts: s.contacts || [],
        });
        setModalOpen(true);
      })
      .catch(() => message.error('无法加载供应商详情'));
  };

  const onFinish = (values: Record<string, unknown>) => {
    const rawContacts = (values.contacts as SupplierContactDTO[] | undefined) || [];
    const contacts = rawContacts.filter(
      (c) => c.contactName?.trim() || c.phoneNumber?.trim() || c.remark?.trim()
    );
    const dto: Partial<SupplierDTO> = {
      supplierId: values.supplierId as string,
      supplierName: values.supplierName as string,
      address: values.address as string | undefined,
      contacts,
    };
    const submit = editingId ? supplierApi.update(editingId, dto) : supplierApi.create(dto);
    submit
      .then(() => {
        message.success(editingId ? '更新成功' : '创建成功');
        setModalOpen(false);
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '操作失败'));
  };

  const onDelete = (id: string) => {
    supplierApi
      .delete(id)
      .then(() => {
        message.success('删除成功');
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '删除失败'));
  };

  const downloadTemplate = () => {
    supplierApi
      .downloadTemplate()
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = 'kfy-supplier-list.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => message.error('下载模板失败'));
  };

  return (
    <>
      <Card
        title="供应商列表"
        extra={
          <Space>
            <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>
              下载模板
            </Button>
            <Button icon={<UploadOutlined />} onClick={() => setImportOpen(true)}>
              导入
            </Button>
            <Button type="primary" onClick={openAdd}>
              新增供应商
            </Button>
          </Space>
        }
      >
        <FilterDrawer variant="space" style={{ marginBottom: 12 }}>
          <Input.Search
            allowClear
            placeholder="供应商名称"
            style={{ width: 200 }}
            onSearch={(v) => setFilter((f) => ({ ...f, name: v || undefined }))}
          />
          <Input.Search
            allowClear
            placeholder="供应商标识"
            style={{ width: 200 }}
            onSearch={(v) => setFilter((f) => ({ ...f, code: v || undefined }))}
          />
          <Input.Search
            allowClear
            placeholder="联系人"
            style={{ width: 200 }}
            onSearch={(v) => setFilter((f) => ({ ...f, contact: v || undefined }))}
          />
        </FilterDrawer>
        <AdaptiveTable
          loading={loading}
          dataSource={filteredList}
          rowKey="id"
          scroll={{ x: 800 }}
          pagination={clientTablePagination()}
          columns={[
            { title: '供应商标识', dataIndex: 'supplierId', width: 140, ellipsis: true },
            { title: '供应商名称', dataIndex: 'supplierName', width: 140, ellipsis: true },
            {
              title: '联系人',
              key: 'contacts',
              width: 140,
              ellipsis: true,
              render: (_, r) => contactSummary(r.contacts),
            },
            {
              title: '联系人数',
              key: 'contactCount',
              width: 90,
              render: (_, r) => r.contacts?.length || 0,
            },
            { title: '地址', dataIndex: 'address', width: 200, ellipsis: true },
            {
              title: '操作',
              width: 160,
              fixed: 'right',
              render: (_, r) => (
                <>
                  <Button type="link" size="small" onClick={() => openEdit(r)}>
                    编辑
                  </Button>
                  <Popconfirm
                    title="确定要删除该供应商吗？"
                    onConfirm={() => r.id && onDelete(r.id)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Button type="link" size="small" danger>
                      删除
                    </Button>
                  </Popconfirm>
                </>
              ),
            },
          ]}
        />
      </Card>
      <Modal
        title={editingId ? '编辑供应商' : '新增供应商'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={720}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="supplierId"
            label="供应商标识"
            rules={[{ required: true, message: '请输入供应商标识' }]}
          >
            <Input placeholder="供应商标识" disabled={!!editingId} />
          </Form.Item>
          <Form.Item
            name="supplierName"
            label="供应商名称"
            rules={[{ required: true, message: '请输入供应商名称' }]}
          >
            <Input placeholder="供应商名称" />
          </Form.Item>
          <Form.Item name="address" label="供应商地址">
            <Input.TextArea placeholder="供应商地址" rows={2} />
          </Form.Item>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>联系人（可选，可多条）</div>
          <Form.List name="contacts">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <div key={key} style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
                    <Row gutter={12}>
                      <Col span={12}>
                        <Form.Item {...rest} name={[name, 'contactName']} label="联系人姓名">
                          <Input placeholder="联系人姓名" />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item {...rest} name={[name, 'phoneNumber']} label="电话">
                          <Input placeholder="电话" />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item {...rest} name={[name, 'remark']} label="备注">
                          <Input placeholder="备注" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Button type="text" danger onClick={() => remove(name)}>
                      删除此联系人
                    </Button>
                  </div>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block>
                    + 添加联系人
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
          <Form.Item style={{ marginBottom: 0, marginTop: 16 }}>
            <Button type="primary" htmlType="submit">
              保存
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={() => setModalOpen(false)}>
              取消
            </Button>
          </Form.Item>
        </Form>
      </Modal>
      <SupplierImportModal open={importOpen} onClose={() => setImportOpen(false)} onSuccess={load} />
    </>
  );
};

export default SupplierManage;
